import axios from "axios";
import moment from "moment";
import React, { useCallback, useEffect, useState } from 'react';
import {
  Area,
  AreaChart,
  Brush,
  CartesianGrid,
  Label,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import ClipLoader from 'react-spinners/ClipLoader';

const CHART_DATA_LIMIT = 10000;

const getRecordTime = (entry) =>
  moment(entry.date ?? entry['Unnamed: 0'] ?? entry.timestamp).valueOf();

function LiveAreaChart(props) {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [seriesConfig, setSeriesConfig] = useState({});

  // Normalize data record for consistent key access
  const normalizeRecord = (entry) => ({
    date: moment(entry.date).format('MM-DD-YYYY'),
    index1: entry.val_1,
    index2: entry.val_2,
    index3: entry.val_3,
  });

  // Fetch forecast data
  const fetchData = useCallback(
    async (type) => {
      try {
        const url =
          type === 'assets'
            ? `${process.env.REACT_APP_BASE_URL}/assets/${props?.id}/forecast_graph?skip=0&limit=${CHART_DATA_LIMIT}`
            : `${process.env.REACT_APP_BASE_URL}/portfolios/${props?.id}/forecast_graph?skip=0&limit=${CHART_DATA_LIMIT}`;

        const response = await axios.get(url);
        if (response.status === 200) {
          const normalized = [...(response?.data || [])]
            .sort((a, b) => getRecordTime(a) - getRecordTime(b))
            .map((entry) => normalizeRecord(entry));
          setData(normalized);
        }
      } catch (error) {
        console.error('Error fetching data:', error);
        setData([]);
      }
    },
    [props?.id]
  );

  // Fetch dynamic series configs with context=forecast
  const fetchConfigs = useCallback(
    async (type) => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_BASE_URL}/display-configs/${props.id}?scope=${type}&context=forecasts`,
          {
            headers: {
              Authorization: `Bearer ${process.env.REACT_APP_TOKEN}`,
            },
          }
        );
        if (response.status === 200) {
          setSeriesConfig(response.data);
        }
      } catch (err) {
        console.error('Error fetching configs:', err);
        setSeriesConfig({});
      }
    },
    [props.id]
  );

  const fetchPortFolioData = useCallback(async () => {
    setLoading(true);
    if (props.resourceType) {
      const type = props.resourceType === 'portfolio' ? 'portfolio' : 'assets';
      await fetchData(type);
      await fetchConfigs(type === 'portfolio' ? 'portfolio' : 'asset');
      setLoading(false);
      return;
    }

    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BASE_URL}/portfolios/?skip=0&limit=1000`
      );
      if (response.status === 200) {
        const isPortfolio = response?.data?.data?.some(
          (d) => d.id === props.id
        );
        await fetchData(isPortfolio ? 'portfolio' : 'assets');
        await fetchConfigs(isPortfolio ? 'portfolio' : 'asset');
      }
    } catch (error) {
      console.error('Error fetching portfolio data:', error);
    } finally {
      setLoading(false);
    }
  }, [props.id, props.resourceType, fetchData, fetchConfigs]);

  useEffect(() => {
    if (props?.id) {
      fetchPortFolioData();
    }
  }, [props?.id, fetchPortFolioData]);

  const hasSeriesData = (key) =>
    data.some(
      (record) => record[key] !== null && record[key] !== undefined
    );

  const showIndex1 = hasSeriesData('index1');
  const showIndex2 = hasSeriesData('index2');
  const showIndex3 = hasSeriesData('index3');
  const showLegend = showIndex1 || showIndex2 || showIndex3;

  // Safe axis color for light/dark mode
  const axisFill = props.theme === 'light' ? undefined : '#ffffff';

  return (
    <>
      {loading ? (
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: 400,
            width: '100%',
          }}
        >
          <ClipLoader size={45} color="#005687" />
        </div>
      ) : (
        <ResponsiveContainer width="95%" height={400}>
          <AreaChart
            data={data}
            margin={{ top: 10, right: 30, left: 30, bottom: 30 }}
          >
            <defs>
              <linearGradient id="colorIndex1" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#005687" stopOpacity={0.8} />
                <stop offset="95%" stopColor="#005687" stopOpacity={0} />
              </linearGradient>
              <linearGradient id="colorIndex2" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#8884d8" stopOpacity={0.8} />
                <stop offset="95%" stopColor="#8884d8" stopOpacity={0} />
              </linearGradient>
              <linearGradient id="colorIndex3" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#ffc658" stopOpacity={0.8} />
                <stop offset="95%" stopColor="#ffc658" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" />

            <XAxis
              dataKey="date"
              tick={{ textAnchor: 'end', angle: -30, fill: axisFill }}
              height={80}
              minTickGap={50}
            >
              <Label
                value="Date"
                offset={-20}
                position="bottom"
                style={{ fill: axisFill }}
              />
            </XAxis>

            <YAxis
              tick={{ fill: axisFill }}
              label={{
                fill: axisFill,
                value: seriesConfig?.y_axis || 'Metrics Value',
                angle: -90,
                position: 'insideLeft',
                style: { textAnchor: 'middle', fill: axisFill },
              }}
            />

            <Tooltip wrapperClassName="custom-tooltip" />
            {showLegend && <Legend />}
            <Brush dataKey="date" height={25} stroke="#005687" />
            {showIndex1 && (
              <Area
                connectNulls
                type="monotone"
                dataKey="index1"
                name={seriesConfig?.index1 || 'MQE Strategy'}
                stroke="#005687"
                fill="url(#colorIndex1)"
              />
            )}
            {showIndex2 && (
              <Area
                connectNulls
                type="monotone"
                dataKey="index2"
                name={seriesConfig?.index2 || 'S&P 500'}
                stroke="#8884d8"
                fill="url(#colorIndex2)"
              />
            )}
            {showIndex3 && (
              <Area
                connectNulls
                type="monotone"
                dataKey="index3"
                name={seriesConfig?.index3 || 'Exchange Rate'}
                stroke="#ffc658"
                fill="url(#colorIndex3)"
              />
            )}
          </AreaChart>
        </ResponsiveContainer>
      )}
    </>
  );
}

export default LiveAreaChart;
